import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { get } from "lodash";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { reduxForm } from "redux-form";
import { NotificationManager } from "react-notifications";

import PaginatedTable from "../../../../sharedComponents/tableWithPagination";
import RecipeNutritionFacts from "./recipeNutritionFacts";
import FilterPanel, { PanelBody } from "../../../../sharedComponents/filterPanel";
import SearchIngredientsForm from "./searchIngredientsForm";
import SelectIngredientsForm from "./selectIngredientsForm";
import EditIngredientsForm from "./editIngredientsForm";
import {
	getRecipeIngredientList,
	removeRecipeIngredient,
	addRecipeIngredients,
	resetField,
	getRecipeNutritionFacts
} from "../../../../store/actions";
import ConfirmDelete from "../../../../sharedComponents/confirmDelete";
import CustomTooltip from "../../../../sharedComponents/tooltip";

const Ingredients = ({
	setActiveTab,
	recipeDetails,
	searchIngredientResults,
	getRecipeIngredientList,
	recipeIngredientList,
	removeRecipeIngredient,
	addRecipeIngredients,
	resetField,
	getRecipeNutritionFacts,
	recipeNutritionFacts,
	initialValues,
	newAdditions
}) => {
	useEffect(() => {
		return () => {
			resetField("AddRecipe.searchIngredientResults", []);
		};
	}, [resetField]);

	useEffect(() => {
		recipeDetails.id && getRecipeIngredientList(recipeDetails.id);
		recipeDetails.id && getRecipeNutritionFacts(recipeDetails.id);
	}, [recipeDetails, getRecipeIngredientList, getRecipeNutritionFacts]);

	const [recipeIngredients, updateRecipeIngredients] = useState([]);
	useEffect(() => {
		updateRecipeIngredients(recipeIngredientList);
	}, [recipeIngredientList]);

	const [searchLoading, setSearchLoading] = useState(false);
	useEffect(() => {
		if (searchIngredientResults && searchIngredientResults.length) {
			setSearchLoading(false);
		}
	}, [searchIngredientResults, setSearchLoading]);

	useEffect(() => {
		newAdditions("ingredients", recipeIngredients.filter((item) => item.isNew).length > 0);
	}, [recipeIngredients, newAdditions]);

	const [submitting, setSubmitting] = useState(false);

	const [showAddIngredientsPanel, toggleAddIngredientsPanel] = useState(false);
	const [showEditIngredientsPanel, toggleEditIngredientsPanel] = useState(false);

	const columns = [
		{
			Header: "Seq",
			accessor: "seq",
			width: 100
		},
		{
			Header: "Main",
			accessor: "main",
			Cell: (props) => {
				return props.value ? <i className="icon icon-check text-primary" /> : null;
			},
			width: 80,
			sortable: false
		},
		{
			Header: "Qty",
			accessor: "quantity",
			width: 100
		},
		{
			Header: "Measure",
			id: "measure_data",
			accessor: (d) => {
				return d.measure_data.measure;
			},
			width: 200
		},
		{
			Header: "Ingredient",
			id: "ingredient",
			accessor: (d) => {
				return d.ingredient_data.name ? d.ingredient_data.name : d.ingredient_data.long_desc;
			}
		},
		{
			Header: "USDA#",
			accessor: "measure",
			width: 140,
			Cell: ({ value }) => {
				return parseInt(value);
			}
		},
		{
			Header: "",
			width: 70,
			id: "actions",
			accessor: (d) => {
				return d.ingredient;
			},
			Cell: ({ value, original }) => (
				<OverlayTrigger trigger="click" placement={value > 2 ? "top" : "bottom"} rootClose overlay={popover(original)}>
					<button className="btn btn-default btn-icon btn-sm">
						<CustomTooltip content="Actions" placement="left">
							<i className="icon icon-more" />
						</CustomTooltip>
					</button>
				</OverlayTrigger>
			),
			sortable: false
		}
	];

	const [ingredientToEdit, updateIngredientToEdit] = useState({});

	const openEditIngredientsPanel = (original) => {
		updateIngredientToEdit(original);
		toggleEditIngredientsPanel(true);
	};

	const popover = (original) => (
		<Popover>
			<div className="dropdown-list">
				<button type="button" className="dropdown-item" onClick={() => openEditIngredientsPanel(original)}>
					<i className="icon icon-add-edit" /> Edit
				</button>
				<button type="button" className="dropdown-item" onClick={() => confirmDelete(original)}>
					<i className="icon icon-trash" /> Delete
				</button>
			</div>
		</Popover>
	);

	const [isExists, setIsExists] = useState(false);

	const addToRecipeIngredients = (event) => {
		const isExists = recipeIngredients.filter((item) => item.ingredient === event.ingredient)[0];
		setIsExists(isExists);
		setSubmitting(false);
		if (isExists) {
			NotificationManager.error("Please select another ingredient!", "", 5000);
		} else {
			updateRecipeIngredients([...recipeIngredients, { ...event, isNew: true }]);
		}
	};

	const updateIngredient = (event) => {
		const updatedList = recipeIngredients.map((item) => (item = item.ingredient === event.ingredient ? event : item));
		updateRecipeIngredients(updatedList);
		toggleEditIngredientsPanel(false);
	};

	const removeIngredient = (original) => {
		const successHandler = () =>
			updateRecipeIngredients(recipeIngredients.filter((item) => item.ingredient !== original.ingredient));
		if (original.id) {
			removeRecipeIngredient(original.id, successHandler);
		} else {
			successHandler();
		}
	};

	const submitRecipeIngredients = () => {
		const newIngredients = recipeIngredients.filter((item) => !item.id);
		const listItems = newIngredients.map((item) => ({
			recipe: item.recipe,
			ingredient: item.ingredient,
			measure: item.measure_data.id,
			quantity: Number(item.quantity),
			main: item.main,
			seq: Number(item.seq)
		}));
		const successHandler = () => {
			setSubmitting(false);
			NotificationManager.success("Successfully added!", "", 5000);
			setActiveTab(2);
			getRecipeIngredientList(recipeDetails.id);
		};
		if (listItems.length) {
			setSubmitting(true);
			addRecipeIngredients(listItems, successHandler);
		} else {
			setActiveTab(2);
		}
	};

	const [deleteItem, setDeleteItem] = useState(null);
	const [reloadNutritionFacts, setReloadNutritionFacts] = useState(false);
	const confirmDelete = (original) => {
		setDeleteItem(original);
	};
	const confirmEvent = (yes) => {
		yes && removeIngredient(deleteItem);
		yes && setReloadNutritionFacts(!reloadNutritionFacts);
		setDeleteItem(null);
	};

	return recipeDetails && recipeDetails.id ? (
		<React.Fragment>
			<div className="p-4">
				<div className="row">
					<div className="col-9">
						<div className="d-flex align-items-center">
							<h4 className="flex-fill m-0">Ingredients Listing</h4>
							<button
								className="btn btn-primary text-uppercase"
								onClick={() => toggleAddIngredientsPanel(!showAddIngredientsPanel)}>
								<i className="icon icon-plus" /> Add new
							</button>
						</div>
						{initialValues && initialValues.raw_ingredients ? (
							<div className="p-3 border-default my-3">
								<h5>Raw ingredients</h5>
								<div dangerouslySetInnerHTML={{ __html: initialValues.raw_ingredients }} />
							</div>
						) : null}
						<div className="table-container">
							{recipeIngredients && recipeIngredients.length ? (
								<PaginatedTable data={recipeIngredients} columns={columns} />
							) : (
									<div className="alert alert-info text-center mt-4">Please add ingredients!</div>
								)}
						</div>
					</div>
					<div className="col-3">
						{recipeNutritionFacts && recipeNutritionFacts.length ? (
							<div className=" card ">
								<div className="border-default p-4 ">
									<h4 className="title-primary-dark">Nutrition Facts</h4>
									<RecipeNutritionFacts forceReload={reloadNutritionFacts} recipeId={recipeDetails.id} />
								</div>
							</div>
						) : null}
					</div>
				</div>
				<div className="tab-footer row pt-3">
					<div className="col-12 text-right">
						<button className="btn btn-default" type="button" onClick={() => setActiveTab(0)}>
							Back
						</button>
						<button
							className="btn btn-primary-dark"
							onClick={submitRecipeIngredients}
							disabled={!recipeIngredients.length || submitting}>
							Save &amp; Next
						</button>
					</div>
				</div>
			</div>
			<ConfirmDelete item={deleteItem} message={<div>Are you sure you want to delete?</div>} onEvent={confirmEvent} />
			<FilterPanel
				className="add-ingredients-panel"
				show={showAddIngredientsPanel}
				toggle={() => toggleAddIngredientsPanel(!showAddIngredientsPanel)}
				title="Search, Choose, Add"
				closeBtn>
				<PanelBody className="d-flex flex-column">
					<div className="d-flex flex-column min-h-100 flex-fill">
						<div>
							<SearchIngredientsForm onSearch={setSearchLoading} />
						</div>
						<div className="flex-fill d-flex flex-column">
							<SelectIngredientsForm
								recipe={recipeDetails}
								togglePanel={(event) => toggleAddIngredientsPanel(event)}
								isSearching={searchLoading}
								onSubmit={(event) => addToRecipeIngredients(event)}
								isExists={isExists}
								setIsExists={(event) => setIsExists(event)}
							/>
						</div>
					</div>
				</PanelBody>
			</FilterPanel>

			<FilterPanel
				className="edit-ingredients-panel"
				show={showEditIngredientsPanel}
				toggle={() => toggleEditIngredientsPanel(!showEditIngredientsPanel)}
				title="Edit ingredient"
				closeBtn>
				<PanelBody className="d-flex flex-column">
					<EditIngredientsForm
						initialValues={ingredientToEdit}
						togglePanel={(event) => toggleEditIngredientsPanel(event)}
						onUpdate={(event) => updateIngredient(event)}
					/>
				</PanelBody>
			</FilterPanel>
		</React.Fragment>
	) : null;
};

const mapStateToProps = (state) => {
	return {
		recipeDetails: get(state, "Recipes.recipeDetails", []),
		searchIngredientResults: get(state, "AddRecipe.searchIngredientResults.results", null),
		recipeIngredientList: get(state, "AddRecipe.recipeIngredientList.results", []),
		recipeNutritionFacts: get(state, "Recipes.recipeNutritionFacts", [])
	};
};

const mapDispatchToProps = {
	getRecipeIngredientList,
	removeRecipeIngredient,
	addRecipeIngredients,
	resetField,
	getRecipeNutritionFacts
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(
	reduxForm({
		form: "Ingredients",
		enableReinitialize: true
	})(Ingredients)
);
